import { computed, makeObservable } from 'mobx'
import Job, { formatWithLegendPriceUnit } from './Job'
import { PublishedLegend } from '../legends/PublishedLegend'
import { DraftPricingDTO } from './dtos/draft-pricing'

/**
 * Summary
 */
export default class Summary {
  /**
   * Coupon IDs.
   */
  readonly couponIds

  /**
   * Unit formatter.
   * @private
   */
  readonly formatUnit: UnitFormatter

  constructor(
    private draftPricingDTO: DraftPricingDTO,
    private job: Job,
    private legend: PublishedLegend,
  ) {
    makeObservable(this)
    this.couponIds = this.draftPricingDTO.coupon_ids
    this.formatUnit = createUnitFormatter(this.legend)
  }

  /**
   * Whether we can view pricing.
   *
   * @private
   */
  @computed
  private get shouldShowPrice() {
    return this.job.canViewPrice && !this.legend.skipQuote
  }

  @computed
  get jobScopeItemsNames() {
    return this.draftPricingDTO.job_scope_item_names
  }

  /**
   * Summary items.
   */
  @computed
  get items(): Array<SummaryItem | SummaryDivider> {
    if (!this.shouldShowPrice) {
      return []
    }
    return mapItems(this.formatUnit, this.draftPricingDTO)
  }

  /**
   * Pricing information.
   */
  @computed
  get pricing(): PricingInfo | null {
    if (!this.shouldShowPrice) {
      return null
    }

    return mapToPricing(this.formatUnit, this.draftPricingDTO)
  }
}

/**
 * Summary item.
 */
export interface SummaryItem {
  type: 'Item'
  /**
   * The item label.
   */
  label: string
  /**
   * The item value.
   */
  value: string
  /**
   * Whether the item is highlighted.
   */
  highlighted?: boolean
}

/**
 * Divider.
 */
export interface SummaryDivider {
  type: 'Divider'
}

/**
 * Pricing information.
 */
export interface PricingInfo {
  /**
   * The total price, formatted accordingly.
   */
  total: string

  /**
   * The amount due now, formatted accordingly.
   */
  amountDueNow: string

  /**
   * The discount amount, formatted accordingly.
   */
  discount: string
  /**
   * The primary amount, formatted accordingly.
   */
  primaryAmount: string
}

/**
 * Unit formatter sig.
 */
type UnitFormatter = (value: number) => string

/**
 * Creates a unit formatter.
 *
 * @param legend
 */
function createUnitFormatter(legend: PublishedLegend): UnitFormatter {
  return (value) => formatWithLegendPriceUnit(legend, value)
}

/**
 * Map to pricing from the DTO.
 */
function mapToPricing(
  formatUnit: UnitFormatter,
  draftPricingDto: DraftPricingDTO,
): PricingInfo | null {
  return {
    total: formatUnit(draftPricingDto.total),
    amountDueNow: formatUnit(draftPricingDto.amount_due_now),
    discount:
      draftPricingDto.coupon_amount === 0
        ? ''
        : formatUnit(draftPricingDto.coupon_amount),
    primaryAmount: formatUnit(draftPricingDto.primary_amount),
  }
}

/**
 * Maps to items from the DTO.
 */
function mapItems(
  formatUnit: UnitFormatter,
  draftPricingDto: DraftPricingDTO,
): Summary['items'] {
  const items: Summary['items'] = []

  if (draftPricingDto.job_scope_item_names.length > 0) {
    items.push(
      {
        type: 'Item',
        label: 'Job Summary',
        value: draftPricingDto.job_scope_item_names.join(`\n`),
      },
      { type: 'Divider' },
    )
  }

  if (draftPricingDto.primary_amount !== 0) {
    items.push({
      type: 'Item',
      label: 'Service Amount',
      highlighted: true,
      value: formatUnit(draftPricingDto.primary_amount),
    })
  }

  return filterSummaryItems(items)
}

/**
 * Filters the summary items so we never:
 *   1. start with a divider
 *   2. end with a divider
 *   3. have consecutive dividers
 *
 * @param items
 */
export function filterSummaryItems(items: Summary['items']) {
  const lastIndex = items.length - 1
  return items.filter((item, index) => {
    if (item.type === 'Divider') {
      // Covers points 1 and 2
      if (index === 0 || index === lastIndex) {
        return false
      }

      // Covers point 3
      // NOTE: We already ruled out that this is NOT the last item,
      // so this is safe.
      const nextItem = items[index + 1]
      if (nextItem.type === 'Divider') {
        return false
      }
    }

    return true
  })
}
